import naja from 'naja';

interface SmartFormConfig {
    clientId: string;
    instanceNumber?: number;
}

interface SmartFormData {
    companyName?: string;
    companyRegistrationNumber?: string;
    companyVatNumber?: string;
    street?: string;
    city?: string;
    zip?: string;
    country?: string;
}

interface SmartFormGlobal {
    beforeInit?: () => void;
    setClientId: (clientId: string) => void;
    onCompanySelected?: (data: SmartFormData, instanceNumber?: number) => void;
    onAddressSelected?: (data: SmartFormData, instanceNumber?: number) => void;
}

declare global {
    interface Window {
        smartform: SmartFormGlobal;
    }
}

export default class SmartFormIntegration {
    private readonly config: SmartFormConfig;
    private companyInput: HTMLInputElement | null;
    private companyNameInput: HTMLInputElement | null;
    private companyIdInput: HTMLInputElement | null;
    private companyVatInput: HTMLInputElement | null;
    private streetInput: HTMLInputElement | null;
    private cityInput: HTMLInputElement | null;
    private zipInput: HTMLInputElement | null;
    private countryInput: HTMLInputElement | null;
    private countrySelectContainer: HTMLElement | null;

    constructor(config: SmartFormConfig) {
        this.config = config;
        // Support OrderCart form fields
        this.companyInput = document.querySelector('input[name="company"]');
        this.streetInput = document.querySelector('input[name="street"]');
        this.cityInput = document.querySelector('input[name="city"]');
        this.zipInput = document.querySelector('input[name="zip"]');
        this.countryInput = document.querySelector('input[name="country"]');
        
        // Support PersonForm fields
        this.companyNameInput = document.querySelector('input[name="person_detail-company_name"]');
        this.companyIdInput = document.querySelector('input[name="person_detail-identification_number"]');
        this.companyVatInput = document.querySelector('input[name="person_detail-tax_number"]');
        
        // If PersonForm fields exist and OrderCart fields don't, use PersonForm fields
        if (!this.streetInput) {
            this.streetInput = document.querySelector('input[name="address-street"]');
        }
        if (!this.cityInput) {
            this.cityInput = document.querySelector('input[name="address-city"]');
        }
        if (!this.zipInput) {
            this.zipInput = document.querySelector('input[name="address-zip"]');
        }
        if (!this.countryInput) {
            this.countryInput = document.querySelector('input[name="address-country_id"]');
        }
        
        this.countrySelectContainer = this.countryInput?.parentElement?.querySelector(
            '.select-container-simple',
        ) as HTMLElement | null;
        
        this.init();
    }

    private init(): void {
        // Initialize SmartForm callbacks
        this.setupSmartFormCallbacks();
        
        // Listen for Naja snippet updates
        this.setupNajaHandlers();
    }

    private setupSmartFormCallbacks(): void {
        if (!window.smartform) {
            window.smartform = {} as SmartFormGlobal;
        }

        // Set up company selection callback
        window.smartform.onCompanySelected = (data: SmartFormData, instanceNumber?: number) => {
            // Only process if it's for our instance
            if (instanceNumber !== this.config.instanceNumber && this.config.instanceNumber !== undefined) {
                return;
            }

            // Fill company name (OrderCart form)
            if (data.companyName && this.companyInput) {
                this.companyInput.value = data.companyName;
                this.triggerInputEvent(this.companyInput);
            }
            
            // Fill company name (PersonForm)
            if (data.companyName && this.companyNameInput) {
                this.companyNameInput.value = data.companyName;
                this.triggerInputEvent(this.companyNameInput);
            }
            
            // Fill company registration number (PersonForm)
            if (data.companyRegistrationNumber && this.companyIdInput) {
                this.companyIdInput.value = data.companyRegistrationNumber;
                this.triggerInputEvent(this.companyIdInput);
            }
            
            // Fill company VAT number (PersonForm)
            if (data.companyVatNumber && this.companyVatInput) {
                this.companyVatInput.value = data.companyVatNumber;
                this.triggerInputEvent(this.companyVatInput);
            }

            // Fill address fields if provided
            if (data.street && this.streetInput) {
                this.streetInput.value = data.street;
                this.triggerInputEvent(this.streetInput);
            }

            if (data.city && this.cityInput) {
                this.cityInput.value = data.city;
                this.triggerInputEvent(this.cityInput);
            }

            if (data.zip && this.zipInput) {
                this.zipInput.value = data.zip;
                this.triggerInputEvent(this.zipInput);
            }

            // Handle country selection if provided
            if (data.country && this.countryInput) {
                const countryCode = this.mapCountryToCode(data.country);
                if (countryCode) {
                    this.updateCountrySelection(countryCode);
                }
            }
        };

        // Set up address selection callback
        window.smartform.onAddressSelected = (data: SmartFormData, instanceNumber?: number) => {
            // Only process if it's for our instance
            if (instanceNumber !== this.config.instanceNumber && this.config.instanceNumber !== undefined) {
                return;
            }

            // Fill address fields
            if (data.street && this.streetInput) {
                this.streetInput.value = data.street;
                this.triggerInputEvent(this.streetInput);
            }

            if (data.city && this.cityInput) {
                this.cityInput.value = data.city;
                this.triggerInputEvent(this.cityInput);
            }

            if (data.zip && this.zipInput) {
                this.zipInput.value = data.zip;
                this.triggerInputEvent(this.zipInput);
            }

            // Handle country selection if provided
            if (data.country && this.countryInput) {
                const countryCode = this.mapCountryToCode(data.country);
                if (countryCode) {
                    this.updateCountrySelection(countryCode);
                }
            }
        };
    }

    private mapCountryToCode(country: string): string | null {
        const countryMap: Record<string, string> = {
            'Česká republika': 'cz',
            'Česko': 'cz',
            'Czech Republic': 'cz',
            'Slovensko': 'sk',
            'Slovakia': 'sk',
            'Polsko': 'pl',
            'Poland': 'pl',
            'Německo': 'de',
            'Germany': 'de',
            'Rakousko': 'at',
            'Austria': 'at',
        };

        return countryMap[country] || null;
    }

    private updateCountrySelection(countryCode: string): void {
        if (!this.countryInput || !this.countrySelectContainer) {
            return;
        }

        // Update hidden input value
        this.countryInput.value = countryCode;

        // Update visible select display
        const options = this.countrySelectContainer.querySelectorAll('.option');
        let selectedText = '';

        options.forEach((option) => {
            const optionElement = option as HTMLElement;
            optionElement.classList.remove('selected');
            
            if (optionElement.getAttribute('data-value') === countryCode) {
                optionElement.classList.add('selected');
                selectedText = optionElement.getAttribute('data-text') || '';
            }
        });

        // Update displayed value
        const selectedValueSpan = this.countrySelectContainer.querySelector('.selected-value');
        if (selectedValueSpan && selectedText) {
            selectedValueSpan.textContent = selectedText;
        }

        // Trigger custom event for country change
        const event = new CustomEvent('selectChange', {
            detail: {
                value: countryCode,
                text: selectedText,
                option: this.countrySelectContainer.querySelector('.option.selected'),
            },
        });
        this.countrySelectContainer.dispatchEvent(event);
    }

    private triggerInputEvent(input: HTMLInputElement): void {
        // Trigger input event for form validation
        const inputEvent = new Event('input', { bubbles: true });
        input.dispatchEvent(inputEvent);

        // Trigger change event for any listeners
        const changeEvent = new Event('change', { bubbles: true });
        input.dispatchEvent(changeEvent);
    }

    private setupNajaHandlers(): void {
        // Re-initialize after snippet updates
        naja.snippetHandler.addEventListener('afterUpdate', (event: { detail: { snippet: { id: string } } }) => {
            if (
                event.detail.snippet.id === 'snippet-orderCartForm-deliveryAddress' ||
                event.detail.snippet.id === 'snippet-orderCartForm-userData'
            ) {
                // Re-get elements after snippet update
                // Support OrderCart form fields
                this.companyInput = document.querySelector('input[name="company"]');
                this.streetInput = document.querySelector('input[name="street"]');
                this.cityInput = document.querySelector('input[name="city"]');
                this.zipInput = document.querySelector('input[name="zip"]');
                this.countryInput = document.querySelector('input[name="country"]');
                
                // Support PersonForm fields
                this.companyNameInput = document.querySelector('input[name="person_detail-company_name"]');
                this.companyIdInput = document.querySelector('input[name="person_detail-identification_number"]');
                this.companyVatInput = document.querySelector('input[name="person_detail-tax_number"]');
                
                // If PersonForm fields exist and OrderCart fields don't, use PersonForm fields
                if (!this.streetInput) {
                    this.streetInput = document.querySelector('input[name="address-street"]');
                }
                if (!this.cityInput) {
                    this.cityInput = document.querySelector('input[name="address-city"]');
                }
                if (!this.zipInput) {
                    this.zipInput = document.querySelector('input[name="address-zip"]');
                }
                if (!this.countryInput) {
                    this.countryInput = document.querySelector('input[name="address-country_id"]');
                }
                
                this.countrySelectContainer = this.countryInput?.parentElement?.querySelector(
                    '.select-container-simple',
                ) as HTMLElement | null;

                // Re-initialize SmartForm if needed
                if (window.smartform && window.smartform.setClientId) {
                    window.smartform.setClientId(this.config.clientId);
                }
            }
        });
    }

    // Static initializer for easy use
    static initialize(clientId: string, instanceNumber: number = 1): SmartFormIntegration {
        return new SmartFormIntegration({ clientId, instanceNumber });
    }
}